import React, { useState, useMemo } from 'react';

// تاریخ MySQL ("YYYY-MM-DD HH:MM:SS") را در همه مرورگرها درست می‌خواند
const toDate = (s) => new Date(String(s || '').replace(' ', 'T'));

const FreeDiscussion = ({ 
    isVisible, 
    user, 
    userProfile, // نام کاربری کاربر لاگین شده
    comments,    // لیست نظرات (شامل پاسخ‌ها) که از App.jsx می‌آید
    isLoadingData, // وضعیت لودینگ از App.jsx
    onAddComment,  // onAddComment(text, parentId?) → Promise<boolean>
    isSubmitting,  // وضعیت در حال ارسال از App.jsx
    onShowAuth,    // باز کردن مودال لاگین اصلی
    Icons 
}) => {
    const [commentText, setCommentText] = useState('');
    const [selectedId, setSelectedId] = useState(null);   // پیام اصلی که در مودال باز است
    const [replyToId, setReplyToId] = useState(null);     // پیامی که به آن پاسخ می‌دهیم
    const [replyText, setReplyText] = useState('');

    // --- ساخت درخت گفتگو: پیام‌های اصلی + پاسخ‌های هر کدام (به‌صورت لیست تخت، مثل تلگرام) ---
    const { roots, repliesByRoot, byId } = useMemo(() => {
        const list = Array.isArray(comments) ? comments : [];
        const byId = new Map(list.map(c => [String(c.id), c]));
        const rootOf = (c) => {
            let cur = c;
            let guard = 0;
            while (cur.parent_id && guard++ < 50) {
                const p = byId.get(String(cur.parent_id));
                if (!p) return null; // والد حذف شده یا هنوز تایید نشده
                cur = p;
            }
            return cur;
        };
        const roots = [];
        const repliesByRoot = {};
        list.forEach(c => {
            if (!c.parent_id) { roots.push(c); return; }
            const r = rootOf(c);
            if (!r) return;
            if (!repliesByRoot[r.id]) repliesByRoot[r.id] = [];
            repliesByRoot[r.id].push(c);
        });
        roots.sort((a, b) => toDate(b.created_at) - toDate(a.created_at));
        Object.values(repliesByRoot).forEach(l => l.sort((a, b) => toDate(a.created_at) - toDate(b.created_at)));
        return { roots, repliesByRoot, byId };
    }, [comments]);

    const selectedRoot = selectedId ? roots.find(r => String(r.id) === String(selectedId)) : null;
    const thread = selectedRoot ? (repliesByRoot[selectedRoot.id] || []) : [];
    const replyTarget = (replyToId && byId.get(String(replyToId))) || selectedRoot;

    const openThread = (root, replyId = null) => {
        setSelectedId(root.id);
        setReplyToId(replyId || root.id);
        setReplyText('');
    };
    const closeThread = () => {
        setSelectedId(null);
        setReplyToId(null);
        setReplyText('');
    };

    // --- ارسال پیام جدید ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user) { onShowAuth(); return; }
        if (!commentText.trim()) return;
        const ok = await onAddComment(commentText.trim());
        if (ok) setCommentText('');
    };

    // --- ارسال پاسخ ---
    const handleReplySubmit = async (e) => {
        e.preventDefault();
        if (!user) { onShowAuth(); return; }
        if (!replyText.trim() || !replyTarget) return;
        const ok = await onAddComment(replyText.trim(), replyTarget.id);
        if (ok) {
            setReplyText('');
            setReplyToId(selectedRoot ? selectedRoot.id : null);
        }
    };

    const AdminBadge = () => (
        <span className="text-[10px] bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full font-bold flex-shrink-0">مدیریت</span>
    );

    // یک پیام داخل مودال (پیام اصلی یا پاسخ)
    const renderMessage = (c, isReply) => {
        const parent = c.parent_id ? byId.get(String(c.parent_id)) : null;
        const showQuote = isReply && parent && selectedRoot && String(parent.id) !== String(selectedRoot.id);
        const isAdminMsg = c.is_admin == 1;
        return (
            <div key={c.id} className={`rounded-2xl border p-4 ${isReply ? 'mr-4 md:mr-8' : ''} ${isAdminMsg ? 'bg-yellow-500/5 border-yellow-500/30' : 'bg-slate-900/50 border-slate-800'}`}>
                <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${isAdminMsg ? 'bg-yellow-500 text-slate-900' : 'bg-slate-800 text-slate-400'}`}>
                        <Icons.User size={16} />
                    </div>
                    <span className="font-bold text-white text-sm truncate">{c.name}</span>
                    {isAdminMsg && <AdminBadge />}
                    <span className="text-[10px] text-slate-500 mr-auto flex-shrink-0">{toDate(c.created_at).toLocaleDateString('fa-IR')}</span>
                </div>
                {showQuote && (
                    <div className="text-[11px] text-slate-500 border-r-2 border-yellow-500/40 pr-2 mb-2 line-clamp-1">
                        در پاسخ به {parent.name}: {parent.text}
                    </div>
                )}
                <p className="text-slate-300 leading-7 whitespace-pre-wrap text-justify text-xs md:text-sm break-words">{c.text}</p>
                <div className="flex justify-end mt-2">
                    <button
                        type="button"
                        onClick={() => { setReplyToId(c.id); }}
                        className="text-xs text-slate-400 hover:text-yellow-500 transition-colors"
                    >
                        پاسخ
                    </button>
                </div>
            </div>
        );
    };

    return (
        <>
            {/* --- Forum Page Content --- */}
            <div id="page-forum" className={`page-section ${isVisible ? '' : 'hidden-page'}`}>
                <div className="container mx-auto px-4 py-12 pb-24 fade-in">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="h-1 w-12 bg-yellow-500 rounded-full"></div>
                        <h3 className="text-3xl font-black text-white">تالار گفت‌و‌گو</h3>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Send Message Section */}
                        <div className="lg:col-span-1">
                            <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-800 p-6 rounded-3xl sticky top-24">
                                <h4 className="text-xl font-bold text-white mb-4">ارسال نظر جدید</h4>
                                <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                                    نظرات، سوالات و تجربیات خود را با دیگر کاربران به اشتراک بگذارید.
                                    <br/>
                                    <span className="text-yellow-500 text-xs">(پیام‌ها پس از تایید نمایش داده می‌شوند)</span>
                                </p>

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <textarea 
                                            value={commentText}
                                            onChange={(e) => setCommentText(e.target.value)}
                                            placeholder={user ? "متن پیام شما..." : "برای ارسال پیام وارد حساب شوید"}
                                            disabled={!user || isSubmitting}
                                            className="w-full bg-slate-950/50 border border-slate-800 text-white rounded-2xl p-4 min-h-[150px] focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 outline-none transition-all resize-none text-sm"
                                        ></textarea>
                                    </div>
                                    <button 
                                        type="submit"
                                        disabled={!user || isSubmitting || !commentText.trim()}
                                        className={`w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all ${
                                            user 
                                            ? 'bg-yellow-500 hover:bg-yellow-400 text-slate-900 shadow-lg shadow-yellow-500/20' 
                                            : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                                        }`}
                                    >
                                        {isSubmitting ? 'در حال ارسال...' : (
                                            <>
                                                <span>ارسال پیام</span>
                                                <Icons.Send size={18} />
                                            </>
                                        )}
                                    </button>
                                    {!user && (
                                        <p className="text-xs text-center text-slate-500 mt-2 cursor-pointer hover:text-yellow-500" onClick={onShowAuth}>
                                            ابتدا باید وارد حساب خود شوید (کلیک کنید)
                                        </p>
                                    )}
                                </form>
                            </div>
                        </div>

                        {/* Comments List */}
                        <div className="lg:col-span-2">
                            <div className="space-y-4">
                                {isLoadingData ? (
                                    <div className="flex flex-col items-center justify-center py-20 text-slate-500 italic">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mb-4"></div>
                                        <span>در حال بارگذاری نظرات...</span>
                                    </div>
                                ) : roots.length === 0 ? (
                                    <div className="bg-slate-900/40 border border-slate-800/50 p-12 rounded-3xl text-center">
                                        <Icons.MessageSquare size={48} className="mx-auto text-slate-700 mb-4 opacity-20" />
                                        <p className="text-slate-500">هنوز نظری ثبت نشده است. اولین نفر باشید!</p>
                                    </div>
                                ) : (
                                    roots.map((comment) => {
                                        const replyCount = (repliesByRoot[comment.id] || []).length;
                                        const isAdminMsg = comment.is_admin == 1;
                                        return (
                                            <div 
                                                key={comment.id} 
                                                onClick={() => openThread(comment)}
                                                className={`hover:bg-slate-900/60 border p-5 rounded-3xl transition-all cursor-pointer group ${isAdminMsg ? 'bg-yellow-500/5 border-yellow-500/30' : 'bg-slate-900/40 border-slate-800/50'}`}
                                            >
                                                <div className="flex items-start gap-4">
                                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors flex-shrink-0 ${isAdminMsg ? 'bg-yellow-500 text-slate-900' : 'bg-slate-800 text-slate-400 group-hover:bg-yellow-500/10 group-hover:text-yellow-500'}`}>
                                                        <Icons.User size={24} />
                                                    </div>
                                                    <div className="flex-grow min-w-0">
                                                        <div className="flex items-center justify-between mb-1 gap-2">
                                                            <div className="flex items-center gap-2 min-w-0">
                                                                <h5 className="font-bold text-white truncate">{comment.name}</h5>
                                                                {isAdminMsg && <AdminBadge />}
                                                            </div>
                                                            <span className="text-[10px] text-slate-500 bg-slate-950 px-2 py-1 rounded-lg flex-shrink-0">
                                                                {toDate(comment.created_at).toLocaleDateString('fa-IR')}
                                                            </span>
                                                        </div>
                                                        <p className="text-slate-400 text-sm line-clamp-2 leading-relaxed">
                                                            {comment.text}
                                                        </p>
                                                        <div className="flex items-center gap-4 mt-3 text-xs">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); openThread(comment); }}
                                                                className="text-slate-400 hover:text-yellow-500 transition-colors"
                                                            >
                                                                پاسخ
                                                            </button>
                                                            {replyCount > 0 && (
                                                                <span className="text-yellow-500/80">{replyCount.toLocaleString('fa-IR')} پاسخ</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* Thread Modal: پیام اصلی + پاسخ‌ها + فرم پاسخ */}
                        {selectedRoot && (
                            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md transition-all" onClick={closeThread}>
                                <div className="bg-slate-900 border border-slate-800 w-full max-w-2xl max-h-[90vh] flex flex-col rounded-3xl p-5 md:p-8 shadow-2xl scale-in" onClick={e => e.stopPropagation()}>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-bold text-white">گفت‌و‌گو</h3>
                                        <button onClick={closeThread} className="text-slate-400 hover:text-red-500 transition-colors">
                                            <Icons.X size={24} />
                                        </button>
                                    </div>

                                    <div className="space-y-3 overflow-y-auto custom-scrollbar flex-grow pb-2">
                                        {renderMessage(selectedRoot, false)}
                                        {thread.map(r => renderMessage(r, true))}
                                    </div>

                                    <form onSubmit={handleReplySubmit} className="mt-4 pt-4 border-t border-slate-800 space-y-3">
                                        {replyTarget && (
                                            <div className="flex items-center justify-between text-xs text-slate-400">
                                                <span>در پاسخ به <b className="text-white">{replyTarget.name}</b></span>
                                                {String(replyTarget.id) !== String(selectedRoot.id) && (
                                                    <button type="button" onClick={() => setReplyToId(selectedRoot.id)} className="text-slate-500 hover:text-yellow-500">
                                                        پاسخ به پیام اصلی
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        <textarea
                                            value={replyText}
                                            onChange={(e) => setReplyText(e.target.value)}
                                            onFocus={() => { if (!user) onShowAuth(); }}
                                            placeholder={user ? "پاسخ شما..." : "برای پاسخ دادن وارد حساب شوید"}
                                            disabled={isSubmitting}
                                            rows={3}
                                            className="w-full bg-slate-950/50 border border-slate-800 text-white rounded-2xl p-3 focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 outline-none transition-all resize-none text-sm"
                                        ></textarea>
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="text-[11px] text-yellow-500/80">(پاسخ‌ها پس از تایید نمایش داده می‌شوند)</span>
                                            <button
                                                type="submit"
                                                disabled={!user || isSubmitting || !replyText.trim()}
                                                className={`px-6 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 transition-all ${
                                                    user && replyText.trim() ? 'bg-yellow-500 hover:bg-yellow-400 text-slate-900' : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                                                }`}
                                            >
                                                {isSubmitting ? 'در حال ارسال...' : (<><span>ارسال پاسخ</span><Icons.Send size={16} /></>)}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default FreeDiscussion;
